/**
 * Centralized Pricing Service
 * Ensures consistent pricing calculations across Designer Tool, Cart, and Checkout
 */

// Pricing calculation constants
const MARKUP_MULTIPLIER = 1.35; // 35% markup on base cost

/**
 * Calculate product pricing based on quantity and shipping rules
 * @param {Object} params - Pricing parameters
 * @param {number} params.baseCost - Base cost from Printify (in cents)
 * @param {number} params.quantity - Product quantity
 * @param {number} params.baseShippingCost - Base shipping cost for first item (in cents)
 * @param {number} params.additionalShippingCost - Additional shipping cost per extra item (in cents)
 * @returns {Object} Calculated pricing breakdown
 */
export const calculateProductPricing = ({
  baseCost = 0,
  quantity = 1,
  baseShippingCost = 0,
  additionalShippingCost = 0
}) => {
  // Calculate product cost with markup
  const baseProductCost = baseCost * MARKUP_MULTIPLIER;
  const totalProductCost = baseProductCost * quantity;
  
  // Calculate shipping cost using formula: baseShipping + (additionalShipping × (quantity - 1))
  const totalShippingCost = baseShippingCost + (additionalShippingCost * (quantity - 1));
  
  // Calculate total cost
  const totalCost = totalProductCost + totalShippingCost;
  
  // Calculate per-unit costs
  const unitProductCost = baseProductCost;
  const unitShippingCost = quantity > 0 ? totalShippingCost / quantity : 0;
  const unitTotalCost = unitProductCost + unitShippingCost;
  
  const pricing = {
    // Raw costs (in cents)
    baseCost,
    baseProductCost,
    totalProductCost,
    baseShippingCost,
    additionalShippingCost,
    totalShippingCost,
    totalCost,
    
    // Per-unit costs (in cents)
    unitProductCost,
    unitShippingCost,
    unitTotalCost,
    
    // Display values (in dollars)
    productPriceDisplay: (totalProductCost / 100).toFixed(2),
    shippingPriceDisplay: (totalShippingCost / 100).toFixed(2),
    totalPriceDisplay: (totalCost / 100).toFixed(2),
    unitProductPriceDisplay: (unitProductCost / 100).toFixed(2),
    unitShippingPriceDisplay: (unitShippingCost / 100).toFixed(2),
    unitTotalPriceDisplay: (unitTotalCost / 100).toFixed(2),
    
    // Quantity breakdown
    quantity,
    firstItemCost: (baseProductCost + baseShippingCost) / 100,
    additionalItemsCost: quantity > 1 ? ((baseProductCost * (quantity - 1)) + (additionalShippingCost * (quantity - 1))) / 100 : 0,
    
    // Formula breakdown for debugging
    formulas: {
      productCost: `${baseProductCost / 100} × ${quantity} = $${(totalProductCost / 100).toFixed(2)}`,
      shippingCost: `${baseShippingCost / 100} + (${additionalShippingCost / 100} × ${quantity - 1}) = $${(totalShippingCost / 100).toFixed(2)}`,
      totalCost: `$${(totalProductCost / 100).toFixed(2)} + $${(totalShippingCost / 100).toFixed(2)} = $${(totalCost / 100).toFixed(2)}`
    }
  };
  
  // Removed detailed pricing logs for security - showing only purchase prices
  console.log('💰 Pricing calculation completed', {
    quantity,
    productPrice: pricing.productPriceDisplay,
    shippingPrice: pricing.shippingPriceDisplay,
    totalPrice: pricing.totalPriceDisplay
  });
  
  return pricing;
};

/**
 * Calculate cart totals from multiple items
 * @param {Array} cartItems - Array of cart items
 * @returns {Object} Cart totals breakdown
 */
export const calculateCartTotals = (cartItems = []) => {
  if (!cartItems?.length) {
    return {
      subtotal: 0,
      shipping: 0,
      total: 0,
      items: 0,
      productCost: 0,
      shippingCost: 0
    };
  }

  let totalProductCost = 0;
  let totalShippingCost = 0;
  let totalItems = 0;

  cartItems.forEach(item => {
    // Product cost (already includes quantity)
    const itemProductCost = item.product_price || 0;
    totalProductCost += itemProductCost;
    
    // Shipping cost (already includes quantity)
    const itemShippingCost = item.shipping_cost || 0;
    totalShippingCost += itemShippingCost;
    
    totalItems += item.quantity || 1;
  });

  // Calculate subtotal as product cost + shipping cost
  const subtotal = totalProductCost + totalShippingCost;

  const totals = {
    productCost: totalProductCost / 100, // Convert to dollars
    shippingCost: totalShippingCost / 100, // Convert to dollars
    subtotal: subtotal / 100, // Convert to dollars
    total: subtotal / 100, // Same as subtotal for cart (no tax yet)
    items: totalItems
  };

  // Removed detailed cart calculation logs for security
  console.log('🛒 Cart totals calculated', {
    items: cartItems.length,
    totalItems,
    total: totals.total
  });

  return totals;
};

/**
 * Call backend tax calculation endpoint
 * @param {string} zipCode - User's zipcode
 * @param {number} productCost - Product cost in dollars
 * @param {number} shippingCost - Shipping cost in dollars
 * @returns {Promise<Object>} Tax calculation result from backend
 */
export const calculateTaxFromBackend = async (zipCode, productCost, shippingCost) => {
  try {
    const response = await fetch('/api/v1/printify/tax/calculate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        zip: zipCode,
        product_cost_cents: Math.round(productCost * 100), // Convert to cents
        shipping_cost_cents: Math.round(shippingCost * 100) // Convert to cents
      })
    });

    if (!response.ok) {
      throw new Error(`Tax calculation failed: ${response.status}`);
    }

    const taxData = await response.json();
    // Removed detailed tax calculation logs for security
    console.log('💳 Tax calculation completed');
    
    return {
      success: true,
      taxAmount: taxData.amount_tax / 100, // Convert from cents to dollars
      totalAmount: taxData.amount_total / 100, // Convert from cents to dollars
      taxRate: taxData.rate_used || 0,
      breakdown: taxData.breakdown
    };
  } catch (error) {
    console.error('❌ Tax calculation error:', error);
    return {
      success: false,
      taxAmount: 0,
      totalAmount: productCost + shippingCost,
      taxRate: 0,
      error: error.message
    };
  }
};

/**
 * Calculate checkout totals with dynamic tax calculation
 * @param {Object} params - Checkout parameters
 * @param {number} params.subtotal - Subtotal (product cost + shipping)
 * @param {number} params.shipping - Shipping cost
 * @param {number} params.productCost - Product cost only
 * @param {string} params.zipCode - User's zipcode for tax calculation
 * @param {Object} params.taxData - Pre-calculated tax data (optional)
 * @returns {Promise<Object>} Checkout totals with tax
 */
export const calculateCheckoutTotals = async ({
  subtotal = 0,
  shipping = 0,
  productCost = 0,
  zipCode = null,
  taxData = null
}) => {
  let taxAmount = 0;
  let taxRate = 0;
  let total = subtotal;

  // If we have pre-calculated tax data, use it
  if (taxData && taxData.amount_tax !== undefined) {
    taxAmount = taxData.amount_tax / 100; // Convert from cents to dollars
    total = subtotal + taxAmount;
    console.log('💳 Using pre-calculated tax data');
  }
  // Otherwise, calculate tax from backend if zipcode is provided
  else if (zipCode) {
    // Calculate tax on Product Cost + Shipping (subtotal)
    const taxResult = await calculateTaxFromBackend(zipCode, productCost + shipping, 0);
    if (taxResult.success) {
      taxAmount = taxResult.taxAmount;
      taxRate = taxResult.taxRate;
      total = subtotal + taxAmount;
    } else {
      // Fallback: no tax applied
      total = subtotal;
      console.log('⚠️ Tax calculation failed, no tax applied');
    }
  }
  // No zipcode provided, no tax applied
  else {
    total = subtotal;
    console.log('ℹ️ No zipcode provided, no tax applied');
  }

  const result = {
    subtotal: parseFloat(subtotal.toFixed(2)),
    shipping: parseFloat(shipping.toFixed(2)),
    productCost: parseFloat(productCost.toFixed(2)),
    tax: parseFloat(taxAmount.toFixed(2)),
    total: parseFloat(total.toFixed(2)),
    taxRate: taxRate * 100, // Convert to percentage for display
    zipCode
  };
  
  // Removed detailed checkout totals logs for security
  console.log('💳 Checkout totals calculated', {
    total: result.total,
    taxRate: `${result.taxRate}%`
  });
  
  return result;
};

/**
 * Recalculate item pricing when quantity changes
 * @param {Object} item - Cart item
 * @param {number} newQuantity - New quantity
 * @returns {Object} Updated item with recalculated pricing
 */
export const recalculateItemPricing = (item, newQuantity) => {
  if (!item || newQuantity <= 0) return item;
  
  // Get shipping formula components from misc data
  const baseShippingCost = item.misc?.baseShippingCost || 0;
  const additionalShippingCost = item.misc?.additionalShippingCost || 0;
  
  // Calculate unit product cost
  const unitProductCost = item.product_price || (item.total_cost / Math.max(item.quantity, 1));
  
  // Recalculate shipping cost using the formula
  const newShippingCost = baseShippingCost + (additionalShippingCost * (newQuantity - 1));
  
  // Recalculate total cost
  const newProductCost = unitProductCost * newQuantity;
  const newTotalCost = newProductCost + newShippingCost;
  
  const updatedItem = {
    ...item,
    quantity: newQuantity,
    shipping_cost: newShippingCost,
    total_cost: newTotalCost,
    unit_cost: newTotalCost / newQuantity
  };
  
  // Removed detailed pricing recalculation logs for security
  console.log('🔄 Item pricing recalculated', {
    cartId: item.cart_id || item.transaction_id,
    oldQuantity: item.quantity,
    newQuantity
  });
  
  return updatedItem;
};

/**
 * Format price for display
 * @param {number} priceInCents - Price in cents
 * @returns {string} Formatted price string
 */
export const formatPrice = (priceInCents) => {
  if (typeof priceInCents !== 'number' || isNaN(priceInCents)) return '$0.00';
  return `$${(priceInCents / 100).toFixed(2)}`;
};

/**
 * Parse price from string to cents
 * @param {string} priceString - Price string (e.g., "$23.63")
 * @returns {number} Price in cents
 */
export const parsePrice = (priceString) => {
  if (!priceString) return 0;
  const numericPrice = parseFloat(priceString.replace(/[$,]/g, ''));
  return isNaN(numericPrice) ? 0 : Math.round(numericPrice * 100);
};

/**
 * Validate pricing consistency between components
 * @param {Object} designerPricing - Pricing from designer tool
 * @param {Object} cartPricing - Pricing from cart
 * @param {Object} checkoutPricing - Pricing from checkout
 * @returns {Object} Validation results
 */
export const validatePricingConsistency = (designerPricing, cartPricing, checkoutPricing) => {
  const validation = {
    isConsistent: true,
    discrepancies: [],
    recommendations: []
  };
  
  // Check if product costs match
  if (designerPricing && cartPricing) {
    const designerProductCost = designerPricing.totalProductCost || 0;
    const cartProductCost = cartPricing.productCost || 0;
    
    if (Math.abs(designerProductCost - cartProductCost) > 1) { // Allow 1 cent difference for rounding
      validation.isConsistent = false;
      validation.discrepancies.push({
        type: 'product_cost_mismatch',
        designer: designerProductCost / 100,
        cart: cartProductCost / 100,
        difference: Math.abs(designerProductCost - cartProductCost) / 100
      });
    }
  }
  
  // Check if shipping costs match
  if (designerPricing && cartPricing) {
    const designerShippingCost = designerPricing.totalShippingCost || 0;
    const cartShippingCost = cartPricing.shippingCost || 0;
    
    if (Math.abs(designerShippingCost - cartShippingCost) > 1) {
      validation.isConsistent = false;
      validation.discrepancies.push({
        type: 'shipping_cost_mismatch',
        designer: designerShippingCost / 100,
        cart: cartShippingCost / 100,
        difference: Math.abs(designerShippingCost - cartShippingCost) / 100
      });
    }
  }
  
  // Check if totals match
  if (designerPricing && cartPricing) {
    const designerTotal = designerPricing.totalCost || 0;
    const cartTotal = cartPricing.total || 0;
    
    if (Math.abs(designerTotal - cartTotal) > 1) {
      validation.isConsistent = false;
      validation.discrepancies.push({
        type: 'total_cost_mismatch',
        designer: designerTotal / 100,
        cart: cartTotal / 100,
        difference: Math.abs(designerTotal - cartTotal) / 100
      });
    }
  }
  
  if (!validation.isConsistent) {
    validation.recommendations.push(
      'Pricing inconsistency detected. Please refresh the page and try again.',
      'Ensure all components are using the same pricing service.',
      'Check that quantity changes are properly propagated across components.'
    );
  }
  
  // Removed detailed validation logs for security
  console.log('🔍 Pricing consistency validation completed', {
    isConsistent: validation.isConsistent,
    discrepancyCount: validation.discrepancies.length
  });
  return validation;
};

export default {
  calculateProductPricing,
  calculateCartTotals,
  calculateCheckoutTotals,
  recalculateItemPricing,
  formatPrice,
  parsePrice,
  validatePricingConsistency,
  MARKUP_MULTIPLIER
};
